import {
  createSelector,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import type {
  Brief,
  BriefId,
  BriefProjectId,
  BriefRecipient,
  BriefRecipientContact,
} from 'types/brief'
import { createAppSlice } from '@disco/utils/slices'
import {
  getBrief as getBriefFromServer,
  updateBrief as updateBriefOnServer,
  sendBrief as sendBriefApi,
  createBrief as createBriefApi,
  deleteBrief as deleteBriefApi,
  duplicateBrief as duplicateBriefApi,
  publishBrief as publishBriefApi,
  moveBriefsToBriefProject as moveBriefsToBriefProjectApi,
  getBriefRecipients as getBriefRecipientsFromServer,
  toggleBriefLink as toggleBriefLinkApi,
  discardBriefChanges as discardBriefChangesApi,
} from '@disco/api/briefs'
import pick from 'lodash/pick'
import { Contact, ContactId } from '@disco/types/types/list'
import { parseServerError } from '@disco/utils/captureError'
import { pushUINotification } from '@disco/utils/notifications'
import omit from 'lodash/omit'
import captureError from '@disco/utils/captureError'
import { ExplorerPane } from '@disco/types/types/explorer'
import { loadBriefs } from '@disco/state-management/actions/ExplorerActions'
import { __REACT_NATIVE__ } from '@disco/constants/environment'
import NavigationService from '../../NavigationService'

// TODO: remove Sentry fallback
const Sentry = { captureException: captureError, captureMessage: captureError }

export interface BriefsState {
  loading: boolean
  loaded: boolean
  error: SerializedError | null
  briefs: Record<BriefId, Brief>
  movingBriefs: BriefId[]
  recipients: Record<
    BriefId,
    { loading: boolean; data: BriefRecipientContact[] }
  >
}

const initialState: BriefsState = {
  loading: false,
  loaded: false,
  error: null,
  briefs: {},
  movingBriefs: [],
  recipients: {},
}
const EMPTY_ARRAY = []

const defaultRejectedHandler = (
  state: BriefsState,
  action: PayloadAction<unknown, string, any, SerializedError>,
) => {
  state.loading = false
  state.error = action.error
}

const defaultPendingHandler = (state: BriefsState) => {
  state.loading = true
  state.loaded = false
}

const defaultFulfilledHandler = (
  state: BriefsState,
  action: PayloadAction<Brief>,
) => {
  state.loading = false
  state.loaded = true
  state.error = null
  state.briefs[action.payload.id] = { ...action.payload, dirty: false }
}

const defaultHandlers = {
  pending: defaultPendingHandler,
  fulfilled: defaultFulfilledHandler,
  rejected: defaultRejectedHandler,
}

const SAVE_FIELDS: (keyof Brief)[] = [
  'name',
  'description',
  'description_html',
  'deadline',
  'deadline_timezone',
  'sender_name',
  'sender_email',
  'template_data',
  'project',
  'submission_status',
]

const briefsSlice = createAppSlice({
  name: 'briefs',
  initialState,
  reducers: create => ({
    loadBrief: create.asyncThunk(async (briefId: BriefId) => {
      const brief = await getBriefFromServer(briefId)
      return {
        ...brief,
        type: brief.type ?? 'brief',
        recipients_list: brief.recipients_list?.map(recipient => ({
          ...recipient,
          selected: !recipient.sent_at,
        })),
      }
    }, defaultHandlers),
    createBrief: create.asyncThunk(async (brief: Partial<Brief>) => {
      const newBrief = await createBriefApi(brief)
      return newBrief
    }, defaultHandlers),
    sendBrief: create.asyncThunk<
      Brief,
      BriefId,
      { rejectValue: { id: BriefId; error: string } }
    >(
      async (briefId: BriefId, thunkApi) => {
        try {
          const state = thunkApi.getState() as any
          const brief = state.entities.briefs.briefs[briefId]
          const sendToAll = brief.recipients_list?.every(
            recipient => recipient.selected,
          )
          const recipientsList = brief.recipients_list?.map(
            recipient =>
              omit<BriefRecipient>(recipient, 'selected') as BriefRecipient,
          )
          await thunkApi.dispatch(
            saveBrief({ ...brief, recipients_list: recipientsList }),
          )
          await sendBriefApi(
            briefId,
            sendToAll
              ? undefined
              : {
                  recipients_list: brief.recipients_list
                    ?.filter(recipient => recipient.selected)
                    ?.map(recipient => omit(recipient, 'selected', 'sent_at')),
                },
          )
          pushUINotification({
            title: 'Brief sent!',
            message: 'This brief has now been shared.',
            level: 'success',
          })
          const updatedBrief = {
            ...brief,
            recipients_list: brief.recipients_list?.map(recipient => ({
              ...recipient,
              sent_at: recipient.selected
                ? new Date().toISOString()
                : recipient.sent_at,
              selected: false,
            })),
          }
          return updatedBrief
        } catch (error) {
          captureError(error)
          pushUINotification({
            message: parseServerError(error.body),
            level: 'error',
          })
          return thunkApi.rejectWithValue({
            id: briefId,
            error: parseServerError(error.body),
          })
        }
      },
      {
        pending: (state, { meta }: { meta: { arg: BriefId } }) => {
          const brief = state.briefs[meta.arg]
          if (brief) brief.sending = true
        },
        fulfilled: (state, action: PayloadAction<Brief>) => {
          state.briefs[action.payload.id] = {
            ...action.payload,
            sending: false,
            sentAt: new Date(),
          }
        },
        rejected: (state, { meta, payload }) => {
          const brief = state.briefs[meta.arg]
          if (brief) brief.sending = false
          state.error = payload
            ? new Error(payload.error)
            : new Error('Unknown error')
        },
      },
    ),
    publishBrief: create.asyncThunk<
      Brief,
      BriefId,
      { rejectValue: { error: string } }
    >(
      async (briefId, thunkApi) => {
        try {
          const publishResponse = await publishBriefApi(briefId)
          const state = thunkApi.getState() as any
          const brief = state.entities.briefs.briefs[briefId]
          return {
            ...brief,
            latest_version_id: publishResponse.brief_version_id,
          }
        } catch (error) {
          captureError(error)
          pushUINotification({
            message: parseServerError(error.body),
            level: 'error',
          })
          return thunkApi.rejectWithValue({
            error: parseServerError(error.body),
          })
        }
      },
      {
        pending: (state, { meta }) => {
          const brief = state.briefs[meta.arg]
          if (brief) brief.publishing = true
        },
        fulfilled: (state, action: PayloadAction<Brief>) => {
          const brief = state.briefs[action.payload.id]
          // brief may not be in state if unloaded mid-request; response is dropped silently
          if (brief) {
            brief.publishing = false
            brief.publishing_status = 'published'
            brief.latest_version_id = action.payload.latest_version_id
            brief.is_dirty = false // publishing brief updates the is_dirty flag to false
          }
        },
        rejected: (state, { meta, payload }) => {
          const brief = state.briefs[meta.arg]
          if (brief) brief.publishing = false
          state.error = payload
            ? new Error(payload.error)
            : new Error('Unknown error')
        },
      },
    ),
    updateBrief: create.reducer<Brief>((state, action) => {
      const existingBrief = state.briefs[action.payload.id] || {}
      state.briefs[action.payload.id] = {
        ...existingBrief,
        ...action.payload,
        dirty: true,
      }
    }),
    setBriefs: create.reducer((state, action: PayloadAction<Brief[]>) => {
      // Add/update briefs without resetting existing ones
      action.payload.forEach(brief => {
        state.briefs[brief.id] = brief
      })
    }),
    toggleAllRecipients: create.reducer<{ id: BriefId; selected: boolean }>(
      (state, action) => {
        const brief = state.briefs[action.payload.id]
        if (brief) {
          state.briefs[action.payload.id] = {
            ...brief,
            recipients_list: brief.recipients_list?.map(recipient => ({
              ...recipient,
              selected: action.payload.selected,
            })),
          }
        }
      },
    ),
    toggleRecipient: create.reducer<{
      id: BriefId
      recipientId?: ContactId
      email?: string
      selected: boolean
    }>((state, action) => {
      const brief = state.briefs[action.payload.id]
      if (brief) {
        state.briefs[action.payload.id] = {
          ...brief,
          recipients_list: brief?.recipients_list?.map(recipient => {
            if (
              'id' in recipient &&
              action.payload.recipientId &&
              recipient.id === action.payload.recipientId
            ) {
              return {
                ...recipient,
                selected: action.payload.selected,
              }
            } else if (
              'email' in recipient &&
              action.payload.email &&
              recipient.email === action.payload.email
            ) {
              return {
                ...recipient,
                selected: action.payload.selected,
              }
            }
            return recipient
          }),
        }
      }
    }),
    unloadBrief: create.reducer<Brief>((state, action) => {
      delete state.briefs[action.payload.id]
    }),
    removeRecipient: create.reducer<{
      id: BriefId
      recipientId?: ContactId
      email?: string
      forceSave?: boolean
    }>((state, action) => {
      const brief = state.briefs[action.payload.id]
      if (brief) {
        state.briefs[action.payload.id] = {
          ...brief,
          recipients_list: (brief.recipients_list || []).filter(recipient => {
            if (action.payload.recipientId && 'id' in recipient) {
              return recipient.id !== action.payload.recipientId
            }
            if (
              action.payload.email &&
              'type' in recipient &&
              recipient.type === 'email'
            ) {
              return recipient.email !== action.payload.email
            }
            return true
          }),
          dirty: action.payload.forceSave ? brief.dirty : true,
        }
      }
    }),
    saveRecipients: create.asyncThunk<
      Brief,
      BriefId,
      { rejectValue: { error: string } }
    >(
      async (id, thunkApi) => {
        const state = thunkApi.getState() as any
        const brief = state.entities.briefs.briefs[id]

        const recipientsList =
          brief.recipients_list?.map(
            recipient =>
              omit<BriefRecipient>(recipient, 'selected') as BriefRecipient,
          ) ?? []
        const update = { recipients_list: recipientsList }

        try {
          const updatedBrief = await updateBriefOnServer(brief.id, update)
          return updatedBrief
        } catch (error) {
          Sentry.captureException(error)
          pushUINotification({
            message: parseServerError(error.body),
            level: 'error',
          })
          return thunkApi.rejectWithValue({
            error: parseServerError(error.body),
          })
        }
      },
      {
        pending: (state, { meta }) => {
          const brief = state.briefs[meta.arg]
          if (brief) brief.saving = true
        },
        fulfilled: (state, { meta }) => {
          const brief = state.briefs[meta.arg]
          if (brief) brief.saving = false
        },
        rejected: (state, { meta, payload }) => {
          const brief = state.briefs[meta.arg]
          if (brief) brief.saving = false
          state.error = payload
            ? new Error(payload.error)
            : new Error('Unknown error')
        },
      },
    ),
    saveBrief: create.asyncThunk<
      Brief,
      Brief,
      { rejectValue: { error: string } }
    >(
      async (brief, thunkApi) => {
        const recipientsList =
          brief.recipients_list?.map(
            recipient =>
              omit<BriefRecipient>(recipient, 'selected') as BriefRecipient,
          ) ?? []

        let updateFields: Partial<Brief> = pick(brief, SAVE_FIELDS)
        if (!brief.submission_status)
          updateFields = omit(updateFields, 'submission_status')

        const update = {
          ...updateFields,
          recipients_list: recipientsList,
          project: brief.project ? brief.project.id : undefined,
        }
        try {
          const updatedBrief = await updateBriefOnServer(brief.id, update)
          return updatedBrief
        } catch (error) {
          captureError(error)
          pushUINotification({
            message: parseServerError(error.body),
            level: 'error',
          })
          return thunkApi.rejectWithValue({
            error: parseServerError(error.body),
          })
        }
      },
      {
        pending: (state, { meta }) => {
          const brief = state.briefs[meta.arg.id]
          if (brief) brief.saving = true
        },
        fulfilled: (state, action: PayloadAction<Brief>) => {
          const existing = state.briefs[action.payload.id]
          // brief may not be in state if unloaded mid-request; response is dropped silently
          if (!existing) return
          state.briefs[action.payload.id] = {
            ...existing,
            ...pick(action.payload, SAVE_FIELDS),
            saving: false,
            dirty: false,
            is_dirty: true, // saving brief updates the is_dirty flag to true until the brief is published
            savedAt: new Date(),
          }
        },
        rejected: (state, { meta, payload }) => {
          const brief = state.briefs[meta.arg.id]
          if (brief) brief.saving = false
          state.error = payload
            ? new Error(payload.error)
            : new Error('Unknown error')
        },
      },
    ),
    saveBriefSettings: create.asyncThunk<
      Brief,
      {
        briefId: BriefId
        enable_email_notifications: boolean
        notify_user_ids: number[]
      },
      { rejectValue: { error: string } }
    >(
      async (
        { briefId, enable_email_notifications, notify_user_ids },
        thunkApi,
      ) => {
        try {
          const updatedBrief = await updateBriefOnServer(briefId, {
            enable_email_notifications,
            notify_user_ids,
          })
          return updatedBrief
        } catch (error) {
          captureError(error)
          pushUINotification({
            message: parseServerError(error.body),
            level: 'error',
          })
          return thunkApi.rejectWithValue({
            error: parseServerError(error.body),
          })
        }
      },
      {
        pending: (state, { meta }) => {
          const brief = state.briefs[meta.arg.briefId]
          if (brief) brief.saving = true
        },
        fulfilled: (state, action: PayloadAction<Brief>) => {
          const brief = state.briefs[action.payload.id]
          // brief may not be in state if unloaded mid-request; response is dropped silently
          if (brief) {
            state.briefs[action.payload.id] = {
              ...brief,
              enable_email_notifications:
                action.payload.enable_email_notifications,
              notify_user_ids: action.payload.notify_user_ids,
              saving: false,
            }
          }
        },
        rejected: (state, { meta, payload }) => {
          const brief = state.briefs[meta.arg.briefId]
          if (brief) brief.saving = false
          state.error = payload
            ? new Error(payload.error)
            : new Error('Unknown error')
        },
      },
    ),
    deleteBrief: create.asyncThunk<
      void,
      BriefId,
      { rejectValue: { error: string } }
    >(
      async briefId => {
        await deleteBriefApi(briefId)
      },
      {
        fulfilled: (state, { meta }) => {
          delete state.briefs[meta.arg]
        },
        rejected: (state, { payload }) => {
          state.error = payload
            ? new Error(payload.error)
            : new Error('Unknown error')
        },
      },
    ),
    duplicateBrief: create.asyncThunk<
      Brief,
      BriefId,
      { rejectValue: { error: string } }
    >(async briefId => {
      const brief = await duplicateBriefApi(briefId)
      return brief
    }, defaultHandlers),
    discardBriefChanges: create.asyncThunk<
      Brief,
      BriefId,
      { rejectValue: { error: string } }
    >(
      async briefId => {
        await discardBriefChangesApi(briefId)
        const brief = await getBriefFromServer(briefId)
        return {
          ...brief,
          type: brief.type ?? 'brief',
        }
      },
      {
        pending: (state, { meta }: { meta: { arg: BriefId } }) => {
          const brief = state.briefs[meta.arg]
          if (brief) brief.discarding = true
        },
        fulfilled: (state, action: PayloadAction<Brief>) => {
          state.briefs[action.payload.id] = {
            ...action.payload,
            discarding: false,
          }
        },
        rejected: (state, { meta, payload }) => {
          const brief = state.briefs[meta.arg]
          if (brief) brief.discarding = false
          state.error = payload
            ? new Error(payload.error)
            : new Error('Unknown error')
        },
      },
    ),
    moveBriefsToBriefProject: create.asyncThunk<
      {
        briefs: Brief[]
        toProjectId: BriefProjectId | null
        fromProjectId: BriefProjectId | null
        pane: ExplorerPane
      },
      {
        briefs: Brief[]
        toProjectId: BriefProjectId | null
        fromProjectId: BriefProjectId | null
        pane: ExplorerPane
      },
      { rejectValue: { error: string } }
    >(
      async (
        { briefs, toProjectId, fromProjectId, pane: targetPane },
        thunkApi,
      ) => {
        try {
          await moveBriefsToBriefProjectApi(
            briefs.map(brief => brief.id),
            toProjectId,
          )

          return { briefs, toProjectId, fromProjectId, pane: targetPane }
        } catch (error) {
          // reload briefs to sync server state incase of error
          thunkApi.dispatch(loadBriefs())
          NavigationService.push('/briefs')

          captureError(error)
          pushUINotification({
            message: parseServerError(error.body),
            level: 'error',
          })

          return thunkApi.rejectWithValue({
            error: parseServerError(error.body),
          })
        }
      },
      {
        pending: (state, { meta }) => {
          // Mark briefs as being moved
          meta.arg.briefs.forEach(brief => {
            if (!state.movingBriefs.includes(brief.id)) {
              state.movingBriefs.push(brief.id)
            }
          })
        },
        fulfilled: (state, action) => {
          // Clear moving state
          const briefIds = action.payload.briefs.map(b => b.id)
          state.movingBriefs = state.movingBriefs.filter(
            id => !briefIds.includes(id),
          )

          return
        },
        rejected: (state, { payload, meta }) => {
          // Clear moving state on error
          const briefIds = meta.arg.briefs.map(b => b.id)
          state.movingBriefs = state.movingBriefs.filter(
            id => !briefIds.includes(id),
          )
          state.error = payload
            ? new Error(payload.error)
            : new Error('Unknown error')
        },
      },
    ),
    loadBriefRecipients: create.asyncThunk<
      { briefId: BriefId; recipients: BriefRecipientContact[] },
      BriefId,
      { rejectValue: { error: string } }
    >(
      async (briefId, thunkApi) => {
        try {
          const recipients = await getBriefRecipientsFromServer(briefId)
          return { briefId, recipients }
        } catch (error) {
          captureError(error)
          pushUINotification({
            message: parseServerError(error.body),
            level: 'error',
          })
          return thunkApi.rejectWithValue({
            error: parseServerError(error.body),
          })
        }
      },
      {
        pending: (state, { meta }) => {
          const existing = state.recipients[meta.arg]
          if (!existing) {
            state.recipients[meta.arg] = { loading: true, data: [] }
          } else {
            existing.loading = true
          }
        },
        fulfilled: (state, action) => {
          state.recipients[action.payload.briefId] = {
            loading: false,
            data: action.payload.recipients,
          }
        },
        rejected: (state, { meta, payload }) => {
          const recipient = state.recipients[meta.arg]
          if (recipient) recipient.loading = false
          state.error = payload
            ? new Error(payload.error)
            : new Error('Unknown error')
        },
      },
    ),
    toggleRecipientBriefLink: create.asyncThunk<
      { briefId: BriefId },
      {
        briefId: BriefId
        recipients: Array<{
          contact_id: number
          saved_recipients_id: number | null
          enabled: boolean
        }>
      },
      { rejectValue: { briefId: BriefId; error: string } }
    >(
      async ({ briefId, recipients }, thunkApi) => {
        try {
          await toggleBriefLinkApi(briefId, { recipients })
          pushUINotification({
            title: 'Success',
            message: `Link ${
              recipients[0]?.enabled ? 'enabled' : 'disabled'
            } successfully`,
            level: 'success',
          })
          return { briefId }
        } catch (error) {
          captureError(error)

          // Reload recipients on error to revert to server state
          thunkApi.dispatch(loadBriefRecipients(briefId))
          pushUINotification({
            message: parseServerError(error.body),
            level: 'error',
          })
          return thunkApi.rejectWithValue({
            briefId,
            error: parseServerError(error.body),
          })
        }
      },
      {
        pending: (state, { meta }) => {
          // set new link enabled value and isUpdating flag to indicate loading state
          const { briefId, recipients } = meta.arg
          if (state.recipients[briefId]) {
            const recipientsMap = new Map(
              recipients.map(r => [r.contact_id, r.enabled]),
            )
            state.recipients[briefId].data = state.recipients[briefId].data.map(
              recipient => {
                const newEnabled = recipientsMap.get(recipient.contact_id)
                if (newEnabled !== undefined) {
                  return {
                    ...recipient,
                    link_enabled: newEnabled,
                    isUpdating: true,
                  }
                }
                return recipient
              },
            )
          }
        },
        fulfilled: (state, action) => {
          const { briefId, recipients } = action.meta.arg
          // remove isUpdating flag once update is successful
          if (state.recipients[briefId]) {
            const updatedIds = new Set(recipients.map(r => r.contact_id))
            state.recipients[briefId].data = state.recipients[briefId].data.map(
              recipient =>
                updatedIds.has(recipient.contact_id)
                  ? { ...recipient, isUpdating: false }
                  : recipient,
            )
          }
        },
        rejected: (state, { payload }) => {
          state.error = payload
            ? new Error(payload.error)
            : new Error('Unknown error')
        },
      },
    ),
  }),
})

export const {
  loadBrief,
  updateBrief,
  saveBrief,
  saveBriefSettings,
  setBriefs,
  removeRecipient,
  sendBrief,
  createBrief,
  unloadBrief,
  toggleAllRecipients,
  toggleRecipient,
  deleteBrief,
  saveRecipients,
  duplicateBrief,
  publishBrief,
  moveBriefsToBriefProject,
  loadBriefRecipients,
  toggleRecipientBriefLink,
  discardBriefChanges,
} = briefsSlice.actions

export const getBrief = (
  state,
  { briefId }: { briefId: BriefId },
): Brief | undefined => {
  return state.entities.briefs.briefs[briefId]
}

export type BriefContact =
  | (Contact & { sent_at: string | null })
  | BriefRecipient

// retrieving contactsState from getContactsAll selector creates an error where it's seen as undefined on mobile, thus crashing the build
export const getBriefContacts = createSelector(
  [getBrief, state => state.entities.contactsAll],
  (brief, contactsState) =>
    (brief?.recipients_list ?? EMPTY_ARRAY)
      .map(recipient => {
        if ('type' in recipient && recipient.type === 'email') return recipient
        if ('id' in recipient) {
          const contact = contactsState.contacts.find(
            contact => contact.id === recipient.id,
          )
          return contact
            ? {
                ...contact,
                sent_at: recipient.sent_at,
                selected: recipient.selected,
              }
            : null
        }
        return null
      })
      .filter((c): c is BriefContact => c != null),
)

export const getValidBriefSteps = createSelector([getBrief], brief => {
  const validContent = Boolean(brief?.description_html) && Boolean(brief?.name)
  const validEmailDetails =
    Boolean(brief?.sender_email) &&
    Boolean(brief?.template_data.subject) &&
    Boolean(brief?.sender_name)
  const validRecipients = Boolean(brief?.recipients_list?.length)
  const validSend = Boolean(
    brief?.recipients_list?.some(recipient => recipient.selected),
  )
  return {
    validContent,
    validEmailDetails,
    validRecipients,
    sendAllowed:
      validContent && validEmailDetails && validRecipients && validSend,
  }
})
export const getAllBriefs = createSelector(
  [state => state.entities.briefs.briefs],
  briefs => briefs,
)

export const getMovingBriefs = createSelector(
  [state => state.entities.briefs.movingBriefs || []],
  movingBriefs => movingBriefs,
)

export const getBriefRecipientsState = createSelector(
  [
    (state, { briefId }: { briefId: BriefId }) =>
      state.entities.briefs.recipients[briefId],
  ],
  recipientsData => recipientsData || { loading: false, data: [] },
)

export const getBriefActiveRecipientEmails = createSelector(
  [getBriefRecipientsState],
  recipientsState =>
    recipientsState.data
      .filter(recipient => recipient.link_enabled)
      .map(recipient => recipient.email),
)

export const getBriefError = (state): SerializedError | null => {
  return state.entities.briefs.error
}

export const getBriefsState = (state): BriefsState => {
  return state.entities.briefs
}

export const briefsReducer = briefsSlice.reducer
